<template>
  <div class="chat-bubble-container" :class="props.type">
    <div class="chat-bubble flex flex-col gap-2">
      <div v-if="props.loading" class="typing">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>
      <div v-else style="white-space: pre-wrap" v-html="content.trim()"></div>
      <div v-if="props.params" class="params flex flex-col gap-2 w-fit">
        <div v-for="listType of ['keep', 'measure', 'filter']" :key="listType">
          <div
            v-if="queryStore.queryCols[listType].length > 0"
            class="grid grid-cols-[5rem_1fr] items-center w-fit"
          >
            <b class="capitalize">{{ listType }}:</b>
            <div class="flex gap-2 flex-wrap w-fit">
              <QueryColChip
                v-for="(c, index) of queryStore.queryCols[listType]"
                :key="index"
                v-model="queryStore.queryCols[listType][index]"
                class="chip w-fit !bg-surface-200 dark:!bg-surface-700 col-start-2 col-end-2"
                :editable="false"
              />
            </div>
          </div>
        </div>
        <div class="flex flex-wrap items-center gap-2 mt-1">
          Does this sound good?
          <Button text @click="queryStore.tabValue = 'query'">Edit</Button>
          <run-query-button class="!rounded-3xl" />
        </div>
      </div>
    </div>
    <div v-if="!loading" class="time text-xs text-gray-500">
      {{ props.timestamp }}
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from "vue";
import { useQueryStore } from "@/stores/queryStore";
import QueryColChip from "@/components/information_tabs/query_elements/QueryColChip.vue";
import RunQueryButton from "@/components/RunQueryButton.vue";
import Button from "primevue/button";
import DOMPurify from "dompurify";
import MarkdownIt from "markdown-it";

const md = new MarkdownIt({ breaks: false });
const content = computed(() => DOMPurify.sanitize(md.render(props.message)));

const queryStore = useQueryStore();

const props = defineProps({
  loading: { type: Boolean, default: false },
  message: { type: String, default: "" },
  type: { type: String, default: "user" },
  timestamp: { type: String, default: "" },
  params: { type: Boolean, default: false },
});
</script>

<style scoped>
.chat-bubble-container {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 0.3rem;

  & .chat-bubble {
    padding: 0.8rem 1.2rem;
    width: fit-content;
  }
}

.chat-bubble-container.bot {
  align-items: start;

  & .chat-bubble {
    @apply bg-surface-100 dark:bg-surface-800;
    border-radius: 20px;
    border-bottom-left-radius: 2px;
  }

  & .time {
    text-align: left;
  }
}

.chat-bubble-container.user {
  align-items: end;

  & .chat-bubble {
    @apply bg-primary-50 dark:bg-primary-500 dark:text-black;
    border-radius: 20px;
    border-bottom-right-radius: 2px;
  }

  & .time {
    text-align: right;
  }
}

.typing {
  align-items: center;
  display: flex;
  height: 17px;
}

.typing .dot {
  @apply bg-surface-400 dark:bg-surface-500;
  animation: mercuryTypingAnimation 1.8s infinite ease-in-out;
  border-radius: 50%;
  height: 7px;
  margin-right: 4px;
  vertical-align: middle;
  width: 7px;
  display: inline-block;
}

.typing .dot:nth-child(1) {
  animation-delay: 200ms;
}

.typing .dot:nth-child(2) {
  animation-delay: 300ms;
}

.typing .dot:nth-child(3) {
  animation-delay: 400ms;
}

.typing .dot:last-child {
  margin-right: 0;
}

@keyframes mercuryTypingAnimation {
  0% {
    @apply bg-surface-400 dark:bg-surface-500;
    transform: translateY(0px);
  }
  28% {
    @apply bg-surface-300 dark:bg-surface-600;
    transform: translateY(-7px);
  }
  44% {
    @apply bg-surface-200 dark:bg-surface-700;
    transform: translateY(0px);
  }
}
</style>
